import { useEffect } from "react";
import { Button, Group, Modal, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";

import { useProgramRegistrationParticipantInfoUpdateMutation } from "../../api/programregistration.api";
import { ProgramRegistration } from "../../models/programregistration.models";

interface ParticipantInfoModalProps {
	opened: boolean;
	onClose: () => void;
	programSessionId: number;
	registration: ProgramRegistration | null;
	participantId: string | undefined;
	participantName: string | undefined;
	healthConcerns: string | undefined;
	allergies: string | undefined;
	specialNeeds: string | undefined;
}

interface ParticipantInfoForm {
	healthConcerns: string;
	allergies: string;
	specialNeeds: string;
}

const ParticipantInfoModal = ({
	opened,
	onClose,
	programSessionId,
	registration,
	participantId,
	participantName,
	healthConcerns,
	allergies,
	specialNeeds,
}: ParticipantInfoModalProps) => {
	const updateParticipantInfoMutation = useProgramRegistrationParticipantInfoUpdateMutation(programSessionId);

	const form = useForm<ParticipantInfoForm>({
		initialValues: {
			healthConcerns: "",
			allergies: "",
			specialNeeds: "",
		},
	});

	useEffect(() => {
		if (!opened) {
			return;
		}

		form.setValues({
			healthConcerns: healthConcerns ?? "",
			allergies: allergies ?? "",
			specialNeeds: specialNeeds ?? "",
		});
		form.resetDirty();
		form.resetTouched();
	}, [opened, healthConcerns, allergies, specialNeeds]);

	const handleSubmit = (values: ParticipantInfoForm) => {
		if (!registration || !participantId) {
			return;
		}

		updateParticipantInfoMutation.mutate({
			registration,
			participantId,
			healthConcerns: values.healthConcerns,
			allergies: values.allergies,
			specialNeeds: values.specialNeeds,
		}, {
			onSuccess: () => {
				onClose();
			},
		});
	};

	return (
		<Modal opened={opened} onClose={onClose} title={<Text size="lg" fw="bold">Update Participant Info</Text>}>
			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Stack>
					<TextInput label="Participant" value={participantName ?? ""} readOnly />
					<Textarea label="Health Concerns" placeholder="Enter health concerns" autosize minRows={3} maxRows={6} {...form.getInputProps("healthConcerns")} />
					<Textarea label="Allergies" placeholder="Enter allergies" autosize minRows={3} maxRows={6} {...form.getInputProps("allergies")} />
					<Textarea label="Special Needs" placeholder="Enter special needs" autosize minRows={3} maxRows={6} {...form.getInputProps("specialNeeds")} />
					<Group justify="end">
						<Button type="button" variant="subtle" onClick={onClose}>Cancel</Button>
						<Button type="submit" loading={updateParticipantInfoMutation.isLoading} disabled={!form.isDirty()}>
							Save
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
};

export default ParticipantInfoModal;
